import React, { useCallback, useEffect, useRef } from "react";
import L from "leaflet";
import { GaiaDrawModeType, GAIA_DRAW_MODE } from "../GaiaMap";
import { GeometryProjector } from "@shared/mirage/utils/GeometryProjector";
import { MapViewHelper } from "../../MapView/MapViewHelper";
import { ClonePreviousGeometryRequest, ClonePreviousGeometryResponse } from "@shared/planningmanagement/contracts/IClonePreviousGeometry";

interface UseClonePreviousGeometryParams {
    map: L.DrawMap | null;
    drawMode: GaiaDrawModeType;
    onClonePreviousGeometry?: (req: ClonePreviousGeometryRequest) => Promise<ClonePreviousGeometryResponse>;
    onGeometryClicked?: (geometryJson27700: string) => void;
    featureGroup: React.MutableRefObject<L.FeatureGroup>;
}

export function useClonePreviousGeometry({ map, drawMode, onClonePreviousGeometry, onGeometryClicked, featureGroup }: UseClonePreviousGeometryParams) {
    const layerGroup = useRef<L.LayerGroup>(new L.LayerGroup());
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hiddenLayers = useRef<L.Layer[]>([]);
    const isActive = drawMode === GAIA_DRAW_MODE.CLONE_PREVIOUS;
    const activeRef = useRef(isActive);
    activeRef.current = isActive;

    const fetchAndRender = useCallback(async () => {
        if (!map || !onClonePreviousGeometry || !activeRef.current) {
            return;
        }

        const bounds = map.getBounds();
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();

        const sw27700 = (() => {
            const fc: GeoJSON.FeatureCollection = {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        properties: {},
                        geometry: { type: "Point", coordinates: [sw.lng, sw.lat] },
                    },
                ],
            };
            const projected = GeometryProjector.projectTo27700(fc);
            return (projected.features[0].geometry as GeoJSON.Point).coordinates;
        })();

        const ne27700 = (() => {
            const fc: GeoJSON.FeatureCollection = {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        properties: {},
                        geometry: { type: "Point", coordinates: [ne.lng, ne.lat] },
                    },
                ],
            };
            const projected = GeometryProjector.projectTo27700(fc);
            return (projected.features[0].geometry as GeoJSON.Point).coordinates;
        })();

        const req: ClonePreviousGeometryRequest = {
            minX: sw27700[0],
            minY: sw27700[1],
            maxX: ne27700[0],
            maxY: ne27700[1],
        };

        try {
            const response = await onClonePreviousGeometry(req);
            if (!activeRef.current) {
                return;
            }

            layerGroup.current.clearLayers();

            if (response.geometries) {
                for (const item of response.geometries) {
                    try {
                        const parsed = JSON.parse(item.geometry);
                        const projected = GeometryProjector.projectToWGS(parsed);
                        const baseStyle = MapViewHelper.getLayerStyling("polygon") as L.PathOptions;

                        const geoJsonLayer = L.geoJSON(projected as any, {
                            style: () => ({
                                ...baseStyle,
                                color: "#FF8C00",
                                fillColor: "#FF8C00",
                                fillOpacity: 0.15,
                                weight: 2,
                                dashArray: "5, 5",
                                className: "clone-previous-layer",
                                interactive: true,
                            }),
                            onEachFeature: (_feature, layer) => {
                                (layer as any)._gaiaRole = "clonePrevious";
                                (layer as any)._clonePreviousId = item.id;
                                (layer as any)._clonePreviousGeometry27700 = item.geometry;

                                layer.on("click", (e: L.LeafletMouseEvent) => {
                                    L.DomEvent.stopPropagation(e.originalEvent);
                                    L.DomEvent.preventDefault(e.originalEvent);
                                    if (onGeometryClicked) {
                                        // Clear hidden layers so they won't be restored when mode exits
                                        hiddenLayers.current = [];
                                        onGeometryClicked(item.geometry);
                                    }
                                });

                                layer.bindTooltip(item.name || `Application #${item.id}`, {
                                    sticky: true,
                                    direction: "top",
                                    className: "clone-previous-tooltip",
                                });
                            },
                        });

                        geoJsonLayer.addTo(layerGroup.current);
                    } catch (e) {
                        console.error("Error rendering clone previous geometry:", e);
                    }
                }
            }
        } catch (e) {
            console.error("Error fetching previous geometries:", e);
        }
    }, [map, onClonePreviousGeometry, onGeometryClicked]);

    const debouncedFetch = useCallback(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(() => {
            fetchAndRender();
        }, 300);
    }, [fetchAndRender]);

    useEffect(() => {
        if (!map) {
            return;
        }

        if (isActive) {
            // Hide existing feature group layers so only fetched geometries are visible
            hiddenLayers.current = [];
            featureGroup.current.eachLayer((layer) => {
                hiddenLayers.current.push(layer);
                if (map.hasLayer(layer)) {
                    map.removeLayer(layer);
                }
            });

            layerGroup.current.addTo(map);
            fetchAndRender();
            map.on("moveend", debouncedFetch);
        } else {
            layerGroup.current.clearLayers();
            if (map.hasLayer(layerGroup.current)) {
                map.removeLayer(layerGroup.current);
            }
            map.off("moveend", debouncedFetch);

            // Restore hidden feature group layers
            for (const layer of hiddenLayers.current) {
                if (!map.hasLayer(layer)) {
                    layer.addTo(map);
                }
            }
            hiddenLayers.current = [];
        }

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
            map.off("moveend", debouncedFetch);
        };
    }, [map, isActive, fetchAndRender, debouncedFetch]);

    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
            layerGroup.current.clearLayers();
        };
    }, []);
}
